import { useCallback, useEffect, useState } from 'react'
import { theme } from '../theme'
import { fmtDateTime } from '../utils/datetime'
import { ALL_WORK_STATES } from '../utils/workStates'

/**
 * Settings → Agents. The relay agents, their reported targets, and their credentials.
 *
 * THE ONE DISCLOSURE RULE, which this page exists to make legible. A token appears exactly
 * once, in the response that minted it: `POST /api/settings/agents` and
 * `POST /api/settings/agents/{id}/rotate-token`. Nothing stored can be read back into one —
 * only a digest exists after the response is written — so an operator who loses the value
 * rotates rather than looks it up. The panel below says that plainly, at the moment the
 * value is on screen, because a secret shown without that sentence reads as retrievable.
 *
 * ROTATION BREAKS A RUNNING AGENT, and that consequence is stated at the point of rotation
 * rather than discovered after it: there is no overlap window and no grace period, so the
 * process still holding the old value is refused on its NEXT POLL until it is redeployed.
 * The words come from the server (`consequence`, with a machine-readable
 * `consequence_code`) rather than from a client-side copy that would go stale.
 *
 * DELETE IS REFUSED for an agent that has been handed work, by two ON DELETE RESTRICT
 * constraints, and the API answers 409 with a reason that NAMES `disable` as the
 * alternative. That reason is rendered verbatim: an operator who reads "internal server
 * error", or any generic failure, tries again rather than disabling.
 *
 * WHAT IS DELIBERATELY NOT ON THIS PAGE: `has_token`.
 *
 * The brief for this lane asked me to correct a `has_token` field derived from
 * `token_hash IS NOT NULL` — a NOT NULL column, so the field is true for every row that
 * can exist, a constant dressed as state. The correction was already made: the API has no
 * such field, and `tests/test_agent_admin_routes.py::test_has_token_is_not_a_field_anywhere`
 * pins its absence. So nothing was retired here, and nothing fabricates it: what this page
 * shows in its place is `last_check_in_at` as an AGE — real state, and the thing an
 * operator actually needs before rotating, since rotation takes a LIVE agent down.
 *
 * NEVER-POLLED IS NOT STALE. A NULL check-in renders *never*, not a large age: a credential
 * that was never deployed and a deployment that has stopped answering are different
 * failures with different remedies, and one age would collapse them.
 */

const AGENTS_API = '/api/settings/agents'

type Target = {
  id: string
  load_balancer_name: string
  dns_name: string
  pod?: string | null
  region?: string | null
  environment?: string | null
  environment_recorded: boolean
  healthy_count?: number | null
  last_seen_at?: string | null
}

type Agent = {
  id: string
  name: string
  public_id: string
  enabled: boolean
  last_check_in_at?: string | null
  last_check_in_age_seconds?: number | null
  live: boolean
  reported_aws_account?: string | null
  reported_aws_region?: string | null
  reported_image_version?: string | null
  clock_skew_seconds?: number | null
  targets: Target[]
  work: Record<string, number>
}

type Fleet = {
  agents: Agent[]
  environment_not_recorded: string
  live_within_seconds: number
}

type Disclosure = {
  token: string
  public_id: string
  shown_once: boolean
  consequence_code: string
  consequence: string
}

/** The response's own `detail`, so a stated refusal is not flattened into a generic one. */
async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => null)
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    const joined = detail
      .map(d => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d)))
      .filter(Boolean)
      .join('; ')
    if (joined) return joined
  }
  return `${fallback} (HTTP ${res.status})`
}

/**
 * A check-in as an AGE, with *never* for null.
 *
 * NULL IS NOT A BIG NUMBER. Returning "a long time ago" for an agent that has never polled
 * would describe a deployment that went quiet, which is a different fault with a different
 * fix.
 */
function checkInAge(agent: Agent): string {
  if (agent.last_check_in_at == null || agent.last_check_in_age_seconds == null) return 'never'
  const seconds = Math.max(0, Math.round(agent.last_check_in_age_seconds))
  if (seconds < 90) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** Signed seconds, as last measured. NULL is never measured; 0 is a real reading of none. */
function skewLabel(agent: Agent): string {
  if (agent.clock_skew_seconds == null) return 'never measured'
  const value = agent.clock_skew_seconds
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}s`
}

function Pill({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  const colour = ok ? '#4caf50' : '#9aa0a6'
  return (
    <span
      style={{
        background: `${colour}22`,
        color: colour,
        border: `1px solid ${colour}55`,
        borderRadius: 3,
        padding: '0.1rem 0.45rem',
        fontSize: '0.72rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  )
}

/**
 * The one appearance of a token value.
 *
 * COPY-TO-CLIPBOARD, AND THE SENTENCE BESIDE IT. The consequence text is the server's, so
 * the words an operator reads at the moment of the decision cannot drift from the rule that
 * produced them.
 */
function TokenPanel({ disclosure, onDismiss }: { disclosure: Disclosure; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div
      style={{
        background: theme.bg,
        color: theme.text,
        border: '1px solid #6a5a2a',
        borderRadius: 6,
        padding: '0.9rem',
        marginBottom: '1.2rem',
      }}
    >
      <strong style={{ fontSize: '0.9rem' }}>
        This token is shown once and will not be shown again.
      </strong>
      <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: '0.35rem' }}>
        {disclosure.consequence}
      </div>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.7rem' }}>
        <code
          style={{
            flex: 1,
            background: theme.bgElevated,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            padding: '0.5rem',
            fontSize: '0.8rem',
            wordBreak: 'break-all',
          }}
        >
          {disclosure.token}
        </code>
        <button
          onClick={() => {
            // Best-effort: a clipboard write can be refused by permissions policy, and the
            // value is selectable above either way — so a failure must not look like a
            // successful copy, and must not take the panel down with it.
            navigator.clipboard
              ?.writeText(disclosure.token)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }}
          style={{
            padding: '0.45rem 0.9rem',
            background: theme.primary,
            color: theme.onPrimary,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div style={{ color: theme.textMuted, fontSize: '0.76rem', marginTop: '0.5rem' }}>
        Handle (safe to log): <code>{disclosure.public_id}</code>
      </div>
      <button
        onClick={onDismiss}
        style={{
          marginTop: '0.7rem',
          padding: '0.35rem 0.8rem',
          background: 'transparent',
          color: theme.textMuted,
          border: `1px solid ${theme.border}`,
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: '0.78rem',
        }}
      >
        I have deployed it — dismiss
      </button>
    </div>
  )
}

export default function AgentSettings() {
  const [fleet, setFleet] = useState<Fleet | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [disclosure, setDisclosure] = useState<Disclosure | null>(null)
  const [newName, setNewName] = useState('')
  const [rotating, setRotating] = useState<Agent | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${AGENTS_API}/fleet`)
      if (!res.ok) throw new Error(await readError(res, 'Could not load the agent fleet'))
      setFleet(await res.json())
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function create() {
    setBusy('create')
    setErr(null)
    try {
      const res = await fetch(AGENTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      })
      if (!res.ok) throw new Error(await readError(res, 'Could not create the agent'))
      const body = await res.json()
      setDisclosure(body.token as Disclosure)
      setNewName('')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function setEnabled(agent: Agent, enabled: boolean) {
    setBusy(agent.id)
    setErr(null)
    try {
      const res = await fetch(`${AGENTS_API}/${agent.id}/${enabled ? 'enable' : 'disable'}`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error(await readError(res, 'Could not change the agent'))
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function rotate(agent: Agent) {
    setBusy(agent.id)
    setErr(null)
    try {
      const res = await fetch(`${AGENTS_API}/${agent.id}/rotate-token`, { method: 'POST' })
      if (!res.ok) throw new Error(await readError(res, 'Could not rotate the token'))
      const body = await res.json()
      setDisclosure(body.token as Disclosure)
      setRotating(null)
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function remove(agent: Agent) {
    setBusy(agent.id)
    setErr(null)
    try {
      const res = await fetch(`${AGENTS_API}/${agent.id}`, { method: 'DELETE' })
      if (!res.ok) {
        // THE SERVER'S OWN REASON, verbatim. The 409 for an agent that has relayed work
        // explains that historical batches would point at nothing and NAMES `disable` as
        // what to do instead. A generic "could not delete" sends the operator back to the
        // same button.
        throw new Error(await readError(res, 'Could not delete the agent'))
      }
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const agents = fleet?.agents ?? []
  const notRecorded = fleet?.environment_not_recorded ?? 'not recorded'

  return (
    <div>
      <h2 style={{ margin: '0 0 0.5rem' }}>Agents</h2>
      <p style={{ color: theme.textMuted, fontSize: '0.82rem', marginTop: 0, maxWidth: '46rem' }}>
        An agent is an <code>e2e_agent</code> deployment inside a provider's AWS account. It
        polls for relay work and sends bytes composed here; it holds no fax logic of its own.
        An agent's token is shown once, when it is minted, and cannot be read back afterwards.
      </p>

      {err && (
        <div
          style={{
            background: '#3a1e1e',
            border: '1px solid #6a2a2a',
            borderRadius: 6,
            padding: '0.7rem',
            marginBottom: '1rem',
          }}
        >
          <span style={{ color: '#ef9a9a', fontSize: '0.82rem' }}>{err}</span>
        </div>
      )}

      {disclosure && (
        <TokenPanel disclosure={disclosure} onDismiss={() => setDisclosure(null)} />
      )}

      <div
        style={{
          background: theme.bgAlt,
          color: theme.text,
          border: `1px solid ${theme.border}`,
          borderRadius: 6,
          padding: '0.9rem',
          marginBottom: '1.2rem',
        }}
      >
        <h3 style={{ margin: '0 0 0.6rem', fontSize: '0.9rem' }}>Add an agent</h3>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Operator label, e.g. ccs1 relay (dev)"
            style={{
              flex: 1,
              maxWidth: '22rem',
              padding: '0.45rem',
              background: theme.bg,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
            }}
          />
          <button
            onClick={create}
            disabled={!newName.trim() || busy === 'create'}
            style={{
              padding: '0.45rem 1rem',
              background: newName.trim() ? theme.primary : theme.bgElevated,
              color: newName.trim() ? theme.onPrimary : theme.textMuted,
              border: 'none',
              borderRadius: 4,
              cursor: newName.trim() ? 'pointer' : 'default',
              fontWeight: 600,
            }}
          >
            {busy === 'create' ? 'Creating…' : 'Create and mint a token'}
          </button>
        </div>
        <div style={{ color: theme.textMuted, fontSize: '0.76rem', marginTop: '0.5rem' }}>
          A token is minted as part of creation — an agent cannot exist without one — and the
          value appears only in that one response.
        </div>
      </div>

      {rotating && (
        <div
          style={{
            background: theme.bg,
            color: theme.text,
            border: '1px solid #6a2a2a',
            borderRadius: 6,
            padding: '0.9rem',
            marginBottom: '1.2rem',
          }}
        >
          {/* THE CONSEQUENCE IS AT THE POINT OF THE DECISION. There is no overlap window:
              the stored digest is replaced, so the process still holding the old value is
              refused on its next poll. An operator who learns that afterwards has already
              taken a live agent down. */}
          <strong style={{ fontSize: '0.88rem' }}>
            Rotate {rotating.name}'s token?
          </strong>
          <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: '0.4rem' }}>
            The previous token stops working the moment the new one is minted. The running
            agent will stop authenticating until it is redeployed with the new value —
            there is no grace period, and its next poll will be refused. The new token is
            shown once and cannot be retrieved afterwards.
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.7rem' }}>
            <button
              onClick={() => rotate(rotating)}
              disabled={busy === rotating.id}
              style={{
                padding: '0.45rem 1rem',
                background: theme.primary,
                color: theme.onPrimary,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {busy === rotating.id ? 'Rotating…' : 'Rotate — the running agent will stop authenticating'}
            </button>
            <button
              onClick={() => setRotating(null)}
              style={{
                padding: '0.45rem 0.9rem',
                background: 'transparent',
                color: theme.textMuted,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {agents.length === 0 ? (
        <div
          style={{
            background: theme.bgAlt,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            padding: '0.9rem',
            fontSize: '0.82rem',
            maxWidth: '46rem',
          }}
        >
          <strong>No agents are configured.</strong>
          <div style={{ color: theme.textMuted, marginTop: '0.35rem' }}>
            Add one above to mint its token, then deploy that token to the{' '}
            <code>e2e_agent</code> running in the provider's account.
          </div>
        </div>
      ) : (
        agents.map(agent => (
          <div
            key={agent.id}
            style={{
              background: theme.bgAlt,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              padding: '0.9rem',
              marginBottom: '0.9rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.6rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.92rem' }}>{agent.name}</strong>
                <Pill ok={agent.enabled}>{agent.enabled ? 'enabled' : 'disabled'}</Pill>
                <Pill ok={agent.live}>{agent.live ? 'polling' : 'not polling'}</Pill>
                <code style={{ fontSize: '0.74rem', color: theme.textMuted }}>
                  {agent.public_id}
                </code>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setEnabled(agent, !agent.enabled)}
                  disabled={busy === agent.id}
                  style={{
                    padding: '0.3rem 0.7rem',
                    background: theme.bgElevated,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                  }}
                >
                  {agent.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => setRotating(agent)}
                  disabled={busy === agent.id}
                  style={{
                    padding: '0.3rem 0.7rem',
                    background: theme.bgElevated,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                  }}
                >
                  Rotate token
                </button>
                <button
                  onClick={() => remove(agent)}
                  disabled={busy === agent.id}
                  title="Refused for an agent that has relayed work — disable it instead"
                  style={{
                    padding: '0.3rem 0.7rem',
                    background: theme.bgAlt,
                    color: '#e06c6c',
                    border: '1px solid #6a2a2a',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
                gap: '0.5rem 1.2rem',
                marginTop: '0.7rem',
                fontSize: '0.78rem',
                color: theme.textMuted,
              }}
            >
              <div>
                Last check-in{' '}
                <span style={{ color: theme.text }}>{checkInAge(agent)}</span>
                {agent.last_check_in_at ? ` (${fmtDateTime(agent.last_check_in_at)})` : ''}
              </div>
              <div>
                Clock skew <span style={{ color: theme.text }}>{skewLabel(agent)}</span>
              </div>
              <div>
                Account{' '}
                <span style={{ color: theme.text }}>
                  {agent.reported_aws_account || 'not reported'}
                </span>
              </div>
              <div>
                Region{' '}
                <span style={{ color: theme.text }}>
                  {agent.reported_aws_region || 'not reported'}
                </span>
              </div>
              <div>
                Image{' '}
                <span style={{ color: theme.text }}>
                  {agent.reported_image_version || 'not reported'}
                </span>
              </div>
              <div>
                Targets <span style={{ color: theme.text }}>{agent.targets.length}</span>
              </div>
            </div>

            {/* WORK BY STATE, every state, zeros included: "0 queued" is the useful
                answer, and a state rendered only when non-zero makes an idle agent and a
                missing field look the same. */}
            <div
              style={{
                marginTop: '0.6rem',
                fontSize: '0.78rem',
                color: theme.textMuted,
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              {ALL_WORK_STATES.map(state => (
                <span key={state}>
                  {state} <span style={{ color: theme.text }}>{agent.work?.[state] ?? 0}</span>
                </span>
              ))}
            </div>

            {agent.targets.length > 0 && (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.78rem',
                  marginTop: '0.7rem',
                }}
              >
                <thead>
                  <tr style={{ background: theme.bgElevated, color: theme.text, textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem' }}>Environment</th>
                    <th style={{ padding: '0.4rem' }}>Pod</th>
                    <th style={{ padding: '0.4rem' }}>Load balancer</th>
                    <th style={{ padding: '0.4rem' }}>Region</th>
                    <th style={{ padding: '0.4rem' }}>Healthy (observed)</th>
                    <th style={{ padding: '0.4rem' }}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {agent.targets.map(t => (
                    <tr key={t.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                      {/* The declared words for a missing tag, never a plausible
                          substitute: dev-ccs1 and qa-ccs1 share one account and region. */}
                      <td style={{ padding: '0.4rem' }}>
                        {t.environment_recorded && t.environment ? t.environment : notRecorded}
                      </td>
                      <td style={{ padding: '0.4rem' }}>{t.pod || '—'}</td>
                      <td style={{ padding: '0.4rem' }}>{t.load_balancer_name}</td>
                      <td style={{ padding: '0.4rem' }}>{t.region || '—'}</td>
                      {/* NOT observed is not zero. Zero is a live finding. */}
                      <td style={{ padding: '0.4rem' }}>
                        {t.healthy_count == null ? 'not observed' : t.healthy_count}
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        {t.last_seen_at ? fmtDateTime(t.last_seen_at) : 'never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}
    </div>
  )
}
